import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Alert, Button, DialogContent } from '@mui/material';
import type { Booking, MachineUpdate, PanelState } from '../features/planning/types';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppDialog from '../components/AppDialog';
import { useProjects } from '../features/planning/hooks/useProjects';
import { useMachines } from '../features/planning/hooks/useMachines';
import { useOrgFeatures } from '../features/planning/hooks/useOrgFeatures';
import { useSites } from '../features/planning/hooks/useSites';
import { useSubcontractors } from '../features/planning/hooks/useSubcontractors';
import { useSuppliers } from '../features/planning/hooks/useSuppliers';
import { useCategories } from '../features/planning/hooks/useCategories';
import { useOptions } from '../features/planning/hooks/useOptions';
import { useOrgSettings } from '../features/settings/hooks/useOrgSettings';
import { usePlanningFilters } from '../features/planning/hooks/usePlanningFilters';
import { useMobileFilterDraft } from '../features/planning/hooks/useMobileFilterDraft';
import { useBulkSelection } from '../features/planning/hooks/useBulkSelection';
import { useExportPlanningMachines } from '../features/planning/hooks/useExportPlanningMachines';
import PageHeader from '../components/PageHeader';
import FilterPanel from '../features/planning/components/FilterPanel';
import DateNavigation from '../features/planning/components/DateNavigation';
import PlanningGrid from '../features/planning/components/PlanningGrid';
import SidePanel from '../features/planning/components/SidePanel';
import BookingModal from '../features/planning/components/BookingModal';
import BookingPickupHandinDialog from '../features/planning/components/BookingPickupHandinDialog';
import BulkActionBar from '../features/planning/components/BulkActionBar';
import BulkActionDialog from '../features/planning/components/BulkActionDialog';
import ActiveFilterChipsBar from '../features/planning/components/ActiveFilterChipsBar';
import Spinner from '../components/Spinner';
import { useNotification } from '../context/useNotification';
import { PLANNING_LABELS as LABELS, EXPORT_LABELS } from '../utils/labels';
import { appNow } from '../utils/timezone';
import { hasFullAccess, canManageBookings } from '../utils/roles';
import type { MemberRole } from '../types/roles';

interface PlanningPageProps {
  orgId: string | null;
  selectedProjectId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
}

export default function PlanningPage({ orgId, selectedProjectId, isSuperAdmin, role }: PlanningPageProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const { projects, loading: projectsLoading } = useProjects(orgId);

  const validProjectId = projects.some((p) => p.id === selectedProjectId) ? selectedProjectId : null;
  const currentProjectId = validProjectId ?? projects[0]?.id ?? null;
  const currentProject = projects.find((p) => p.id === currentProjectId);
  const siteOrgId = orgId;

  const {
    machines,
    error: machinesError,
    refetch: refetchMachines,
    updateMachine,
    bulkUpdateMachines,
    deleteMachine,
    reorderMachines,
    addBooking,
    updateBooking,
    confirmBookingPickup,
    confirmBookingHandin,
    deleteBooking,
  } = useMachines(currentProjectId, siteOrgId);
  const { sites, addSite } = useSites(currentProjectId, siteOrgId);
  const { subcontractors, addSubcontractor, addSubcontractorUser } = useSubcontractors(siteOrgId);
  const { suppliers, addSupplier } = useSuppliers(siteOrgId);
  const { categories, addCategory } = useCategories(siteOrgId);
  const { options, addOption } = useOptions(siteOrgId);
  const activeProjects = projects.map((p) => ({ id: p.id, name: p.name, year: p.year }));
  const { settings } = useOrgSettings(siteOrgId);
  const { pickupHandinEnabled } = useOrgFeatures(orgId);
  const filters = usePlanningFilters(machines, sites, pickupHandinEnabled);
  const { notifySuccess, notifyError } = useNotification();

  const mobileFilterDraft = useMobileFilterDraft(filters);
  const bulkSelection = useBulkSelection(bulkUpdateMachines);
  const { exportMachines, exporting: exportingMachines } = useExportPlanningMachines(
    filters.filteredMachines,
    currentProject ? { name: currentProject.name, year: currentProject.year } : undefined,
    options
  );

  const handleBulkSave = async (updates: MachineUpdate) => {
    const ok = await bulkSelection.saveBulkAction(updates);
    if (ok) notifySuccess(LABELS.bulkActionSuccess); else notifyError(LABELS.bulkActionError);
    return ok;
  };

  const handleReorder = (orderedIds: string[]) => {
    reorderMachines(orderedIds).then((ok) => {
      if (ok) notifySuccess(LABELS.reorderSuccess); else notifyError(LABELS.reorderError);
    });
  };

  const handleExportMachines = () => {
    exportMachines()
      .then(() => notifySuccess(EXPORT_LABELS.exportSuccess))
      .catch(() => notifyError(EXPORT_LABELS.exportError));
  };

  const handleAddSupplier = (name: string, email: string) =>
    addSupplier(name, email).then((created) => {
      if (created) notifySuccess(LABELS.supplierAddSuccess); else notifyError(LABELS.mutationError);
      return created;
    });
  const handleAddCategory = (name: string) =>
    addCategory(name).then((created) => {
      if (created) notifySuccess(LABELS.categoryAddSuccess); else notifyError(LABELS.mutationError);
      return created;
    });
  const handleAddOption = (name: string) =>
    addOption(name).then((created) => {
      if (created) notifySuccess(LABELS.optionAddSuccess); else notifyError(LABELS.mutationError);
      return created;
    });
  const handleAddSubcontractor = (name: string) =>
    addSubcontractor(name).then((created) => {
      if (created) notifySuccess(LABELS.subcontractorAddSuccess); else notifyError(LABELS.mutationError);
      return created;
    });
  const handleAddSubcontractorUser = (subcontractorId: string, firstName: string, lastName: string) =>
    addSubcontractorUser(subcontractorId, firstName, lastName).then((created) => {
      if (created) notifySuccess(LABELS.subcontractorUserAddSuccess); else notifyError(LABELS.mutationError);
      return created;
    });
  const handleAddSite = (name: string, projectId: string | null, color: string) =>
    addSite(name, projectId, color).then((created) => {
      if (created) notifySuccess(LABELS.siteAddSuccess); else notifyError(LABELS.mutationError);
      return created;
    });
  const handleConfirmBookingPickup = (bookingId: string, contactUserId: string) =>
    confirmBookingPickup(bookingId, contactUserId)
      .then(() => { notifySuccess(LABELS.pickupConfirmSuccess); return true; })
      .catch(() => { notifyError(LABELS.mutationError); return false; });
  const handleConfirmBookingHandin = (bookingId: string, contactUserId: string) =>
    confirmBookingHandin(bookingId, contactUserId)
      .then(() => { notifySuccess(LABELS.handinConfirmSuccess); return true; })
      .catch(() => { notifyError(LABELS.mutationError); return false; });

  const [viewStart, setViewStart] = useState<string>(() => {
    const preset = (location.state as { viewStart?: string } | null)?.viewStart;
    return preset ?? appNow().subtract(3, 'day').format('YYYY-MM-DD');
  });
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [panelState, setPanelState] = useState<PanelState>(null);
  const [bookingPreset, setBookingPreset] = useState<{
    machineId: string | null;
    startDate: string | null;
  } | null>(null);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [pickupHandinAction, setPickupHandinAction] = useState<{ mode: 'pickup' | 'handin'; booking: Booking } | null>(null);

  const archived = currentProject?.archived ?? false;
  const canEditMachines = !archived && hasFullAccess(role, isSuperAdmin);
  const canEditBookings = !archived && canManageBookings(role, isSuperAdmin);
  const machinesReadOnly = !canEditMachines;
  const bookingsReadOnly = !canEditBookings;

  useEffect(() => {
    if (isMobile || !panelState) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPanelState(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, panelState]);

  const shiftDays = (n: number) => setViewStart((s) => dayjs(s).add(n, 'day').format('YYYY-MM-DD'));
  const goToday = () => setViewStart(appNow().subtract(3, 'day').format('YYYY-MM-DD'));

  const handleSelectAll = () => {
    bulkSelection.selectAll(filters.filteredMachines.map((m) => m.id));
  };

  const startDayjs = dayjs(viewStart);
  const viewLabel = `${startDayjs.date()} ${LABELS.monthsShort[startDayjs.month()]} ${startDayjs.year()}`;

  const panelAriaLabelledBy = panelState?.type === 'booking' ? 'detail-booking-title' : 'detail-machine-name';


  if (projectsLoading && projects.length === 0) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <Spinner size="lg" className="text-primary" />
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col bg-background">
      <PageHeader />

      <div className="flex flex-1 overflow-hidden">
        {!isMobile && (
          <aside className="w-56 flex flex-col flex-shrink-0 border-r border-border bg-surface">
            <FilterPanel
              search={filters.search}
              setSearch={filters.setSearch}
              availabilityFilter={filters.availabilityFilter}
              toggleAvailability={filters.toggleAvailability}
              statusFilter={filters.statusFilter}
              toggleStatus={filters.toggleStatus}
              engineTypeFilter={filters.engineTypeFilter}
              toggleEngineType={filters.toggleEngineType}
              siteFilter={filters.siteFilter}
              toggleSite={filters.toggleSite}
              sites={filters.sortedSites}
              supplierFilter={filters.supplierFilter}
              toggleSupplier={filters.toggleSupplier}
              suppliers={filters.uniqueSuppliers}
              subcontractorFilter={filters.subcontractorFilter}
              toggleSubcontractor={filters.toggleSubcontractor}
              subcontractors={filters.sortedSubcontractors}
              categoryFilter={filters.categoryFilter}
              toggleCategory={filters.toggleCategory}
              categories={filters.sortedCategories}
              optionsFilter={filters.optionsFilter}
              toggleOption={filters.toggleOption}
              options={filters.sortedOptions}
              filteredCount={filters.filteredMachines.length}
              totalCount={machines.length}
              pickupFilter={filters.pickupFilter}
              togglePickup={filters.togglePickup}
              dashboardFilter={filters.dashboardFilter}
              onClearDashboardFilter={() => { filters.clearDashboardFilter(); goToday(); }}
              hideChips
              onExport={handleExportMachines}
              exporting={exportingMachines}
              pickupHandinEnabled={pickupHandinEnabled}
            />
          </aside>
        )}

        <main className="flex-1 flex flex-col overflow-hidden">
          <h1 className="sr-only">Planning</h1>
          <DateNavigation
            viewLabel={viewLabel}
            viewStart={viewStart}
            canEdit={canEditMachines}
            isReorderMode={isReorderMode}
            isBulkMode={bulkSelection.isBulkMode}
            activeFilterCount={filters.activeFilterCount}
            onFilterOpen={() => isMobile ? mobileFilterDraft.openMobileFilter() : filters.setFilterOpen(true)}
            onShiftDays={shiftDays}
            onGoToday={goToday}
            onDateSelect={(date) => setViewStart(date)}
            onAddMachine={() => navigate('/machines/new')}
            onToggleReorderMode={() => setIsReorderMode((v) => !v)}
            onToggleBulkMode={bulkSelection.toggleBulkMode}
          />

          {bulkSelection.isBulkMode && (
            <BulkActionBar
              selectedCount={bulkSelection.selectedBulkIds.size}
              totalCount={filters.filteredMachines.length}
              onSelectAll={handleSelectAll}
              onDeselectAll={bulkSelection.deselectAll}
              onAction={bulkSelection.setBulkDialogAction}
              onPickupConfirm={() => handleBulkSave({ pickup_on_end_date: true })}
              onPickupUnconfirm={() => handleBulkSave({ pickup_on_end_date: false })}
            />
          )}

          {!isMobile && (
            <ActiveFilterChipsBar
              availabilityFilter={filters.availabilityFilter}
              toggleAvailability={filters.toggleAvailability}
              statusFilter={filters.statusFilter}
              toggleStatus={filters.toggleStatus}
              engineTypeFilter={filters.engineTypeFilter}
              toggleEngineType={filters.toggleEngineType}
              siteFilter={filters.siteFilter}
              toggleSite={filters.toggleSite}
              sites={filters.sortedSites}
              supplierFilter={filters.supplierFilter}
              toggleSupplier={filters.toggleSupplier}
              suppliers={filters.uniqueSuppliers}
              subcontractorFilter={filters.subcontractorFilter}
              toggleSubcontractor={filters.toggleSubcontractor}
              subcontractors={filters.sortedSubcontractors}
              categoryFilter={filters.categoryFilter}
              toggleCategory={filters.toggleCategory}
              categories={filters.sortedCategories}
              optionsFilter={filters.optionsFilter}
              toggleOption={filters.toggleOption}
              options={filters.sortedOptions}
              pickupFilter={filters.pickupFilter}
              togglePickup={filters.togglePickup}
              dashboardFilter={filters.dashboardFilter}
              pickupHandinEnabled={pickupHandinEnabled}
            />
          )}

          {machinesError && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={refetchMachines}>
                  {LABELS.retryLoad}
                </Button>
              }
            >
              {machinesError}
            </Alert>
          )}

          <div className="flex flex-1 overflow-hidden">
            <PlanningGrid
              machines={isReorderMode ? machines : filters.filteredMachines}
              viewStart={viewStart}
              selectedId={panelState?.machineId}
              isReadOnly={bookingsReadOnly}
              isReorderMode={isReorderMode}
              isBulkMode={bulkSelection.isBulkMode}
              selectedBulkIds={bulkSelection.selectedBulkIds}
              pickupHandinEnabled={pickupHandinEnabled}
              onSelectMachine={(m) =>
                setPanelState((prev) =>
                  prev?.type === 'machine' && prev.machineId === m.id ? null : { type: 'machine', machineId: m.id }
                )
              }
              onToggleBulkSelect={bulkSelection.toggleBulkSelect}
              onDateClick={(machine, date) =>
                setBookingPreset({ machineId: machine.id, startDate: date })
              }
              onBookingClick={(bk) => setPanelState({ type: 'booking', bookingId: bk.id, machineId: bk.machine_id })}
              onShiftDays={shiftDays}
              onReorder={handleReorder}
              onResizeBooking={(booking, edge, newDate) =>
                updateBooking(booking.id, edge === 'start' ? { start_date: newDate } : { end_date: newDate })
                  .then(() => notifySuccess(LABELS.bookingResizeSuccess))
                  .catch(() => notifyError(LABELS.mutationError))
              }
              onResizeBlocked={() => notifyError(LABELS.bookingResizeBlocked)}
            />
            {!isMobile && (
              <SidePanel
                panelState={panelState}
                machines={machines}
                machinesReadOnly={machinesReadOnly}
                bookingsReadOnly={bookingsReadOnly}
                projectName={currentProject?.name}
                suppliers={suppliers}
                categories={categories}
                options={options}
                orgSettings={settings}
                pickupHandinEnabled={pickupHandinEnabled}
                onClose={() => setPanelState(null)}
                onBackToMachine={(machineId) => setPanelState({ type: 'machine', machineId })}
                onSelectBooking={(bookingId, machineId) => setPanelState({ type: 'booking', bookingId, machineId })}
                onUpdateMachine={(id, updates, related) => updateMachine(id, updates, related)}
                onDeleteBooking={deleteBooking}
                onDeleteMachine={deleteMachine}
                onAddBooking={() =>
                  setBookingPreset({ machineId: panelState?.machineId ?? null, startDate: null })
                }
                onEditBooking={(bk) => setEditingBooking(bk)}
                onAddSupplier={handleAddSupplier}
                onAddCategory={handleAddCategory}
                onAddOption={handleAddOption}
                onConfirmPickup={(bk) => setPickupHandinAction({ mode: 'pickup', booking: bk })}
                onConfirmHandin={(bk) => setPickupHandinAction({ mode: 'handin', booking: bk })}
              />
            )}
          </div>
        </main>
      </div>

      {isMobile && panelState && (
        <AppDialog
          open
          onClose={() => setPanelState(null)}
          aria-labelledby={panelAriaLabelledBy}
          testId="detail-panel-dialog"
        >
          <SidePanel
            panelState={panelState}
            machines={machines}
            machinesReadOnly={machinesReadOnly}
            bookingsReadOnly={bookingsReadOnly}
            projectName={currentProject?.name}
            className="flex flex-col flex-1 overflow-hidden min-h-0"
            suppliers={suppliers}
            categories={categories}
            options={options}
            orgSettings={settings}
            pickupHandinEnabled={pickupHandinEnabled}
            onClose={() => setPanelState(null)}
            onBackToMachine={(machineId) => setPanelState({ type: 'machine', machineId })}
            onSelectBooking={(bookingId, machineId) => setPanelState({ type: 'booking', bookingId, machineId })}
            onUpdateMachine={(id, updates, related) => updateMachine(id, updates, related)}
            onDeleteBooking={deleteBooking}
            onDeleteMachine={deleteMachine}
            onAddBooking={() =>
              setBookingPreset({ machineId: panelState?.machineId ?? null, startDate: null })
            }
            onEditBooking={(bk) => setEditingBooking(bk)}
            onAddSupplier={handleAddSupplier}
            onAddCategory={handleAddCategory}
            onAddOption={handleAddOption}
            onConfirmPickup={(bk) => setPickupHandinAction({ mode: 'pickup', booking: bk })}
            onConfirmHandin={(bk) => setPickupHandinAction({ mode: 'handin', booking: bk })}
          />
        </AppDialog>
      )}

      <AppDialog
        open={filters.filterOpen}
        onClose={mobileFilterDraft.draftFilters ? mobileFilterDraft.closeMobileFilter : () => filters.setFilterOpen(false)}
        title={LABELS.drawerTitle}
        testId="filter-dialog"
      >
        <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <FilterPanel
            search={filters.search}
            setSearch={filters.setSearch}
            availabilityFilter={mobileFilterDraft.draftFilters?.availability ?? filters.availabilityFilter}
            toggleAvailability={mobileFilterDraft.draftFilters ? mobileFilterDraft.toggleDraftAvailability : filters.toggleAvailability}
            statusFilter={mobileFilterDraft.draftFilters?.status ?? filters.statusFilter}
            toggleStatus={mobileFilterDraft.draftFilters ? mobileFilterDraft.toggleDraftStatus : filters.toggleStatus}
            engineTypeFilter={mobileFilterDraft.draftFilters?.engineType ?? filters.engineTypeFilter}
            toggleEngineType={mobileFilterDraft.draftFilters ? mobileFilterDraft.toggleDraftEngineType : filters.toggleEngineType}
            siteFilter={mobileFilterDraft.draftFilters?.site ?? filters.siteFilter}
            toggleSite={mobileFilterDraft.draftFilters ? mobileFilterDraft.toggleDraftSite : filters.toggleSite}
            sites={filters.sortedSites}
            supplierFilter={mobileFilterDraft.draftFilters?.supplier ?? filters.supplierFilter}
            toggleSupplier={mobileFilterDraft.draftFilters ? mobileFilterDraft.toggleDraftSupplier : filters.toggleSupplier}
            suppliers={filters.uniqueSuppliers}
            subcontractorFilter={mobileFilterDraft.draftFilters?.subcontractor ?? filters.subcontractorFilter}
            toggleSubcontractor={mobileFilterDraft.draftFilters ? mobileFilterDraft.toggleDraftSubcontractor : filters.toggleSubcontractor}
            subcontractors={filters.sortedSubcontractors}
            categoryFilter={mobileFilterDraft.draftFilters?.category ?? filters.categoryFilter}
            toggleCategory={mobileFilterDraft.draftFilters ? mobileFilterDraft.toggleDraftCategory : filters.toggleCategory}
            categories={filters.sortedCategories}
            optionsFilter={mobileFilterDraft.draftFilters?.option ?? filters.optionsFilter}
            toggleOption={mobileFilterDraft.draftFilters ? mobileFilterDraft.toggleDraftOption : filters.toggleOption}
            options={filters.sortedOptions}
            filteredCount={filters.filteredMachines.length}
            totalCount={machines.length}
            onClose={mobileFilterDraft.draftFilters ? mobileFilterDraft.closeMobileFilter : () => filters.setFilterOpen(false)}
            onSave={mobileFilterDraft.draftFilters ? mobileFilterDraft.saveMobileFilter : undefined}
            committedFilters={mobileFilterDraft.draftFilters ? {
              availabilityFilter: filters.availabilityFilter,
              statusFilter: filters.statusFilter,
              engineTypeFilter: filters.engineTypeFilter,
              siteFilter: filters.siteFilter,
              supplierFilter: filters.supplierFilter,
              subcontractorFilter: filters.subcontractorFilter,
              categoryFilter: filters.categoryFilter,
              optionsFilter: filters.optionsFilter,
              pickupFilter: filters.pickupFilter,
            } : undefined}
            pickupFilter={mobileFilterDraft.draftFilters?.pickup ?? filters.pickupFilter}
            togglePickup={mobileFilterDraft.draftFilters ? mobileFilterDraft.toggleDraftPickup : filters.togglePickup}
            dashboardFilter={filters.dashboardFilter}
            pickupHandinEnabled={pickupHandinEnabled}
            onClearDashboardFilter={() => { filters.clearDashboardFilter(); mobileFilterDraft.closeMobileFilter(); }}
            onExport={handleExportMachines}
            exporting={exportingMachines}
          />
        </DialogContent>
      </AppDialog>

      {bookingPreset && (
        <BookingModal
          machines={machines}
          sites={sites}
          subcontractors={subcontractors}
          booking={undefined}
          lockedMachineId={bookingPreset.machineId}
          prefilledStartDate={bookingPreset.startDate}
          defaultStartTime={settings.default_start_time}
          defaultEndTime={settings.default_end_time}
          activeProjects={activeProjects}
          canCreateSite={canEditMachines}
          onClose={() => setBookingPreset(null)}
          onSave={(machineId, data) => addBooking(machineId, data)
            .then(() => notifySuccess(LABELS.bookingAddSuccess))
            .catch(() => notifyError(LABELS.mutationError))}
          onAddSubcontractor={handleAddSubcontractor}
          onAddSubcontractorUser={handleAddSubcontractorUser}
          onAddSite={handleAddSite}
        />
      )}
      {pickupHandinAction && (
        <BookingPickupHandinDialog
          mode={pickupHandinAction.mode}
          booking={pickupHandinAction.booking}
          machineBookings={
            machines.find((m) => m.id === pickupHandinAction.booking.machine_id)?.bookings ?? []
          }
          subcontractorUsers={
            subcontractors.find((s) => s.id === pickupHandinAction.booking.subcontractor_id)?.subcontractor_users ?? []
          }
          onClose={() => setPickupHandinAction(null)}
          onConfirm={
            pickupHandinAction.mode === 'pickup' ? handleConfirmBookingPickup : handleConfirmBookingHandin
          }
          onAddSubcontractorUser={handleAddSubcontractorUser}
        />
      )}
      {bulkSelection.bulkDialogAction && (() => {
        const selectedMachines = machines.filter((m) => bulkSelection.selectedBulkIds.has(m.id));
        const endDates = selectedMachines.map((m) => m.end_date).filter(Boolean) as string[];
        const startDates = selectedMachines.map((m) => m.start_date).filter(Boolean) as string[];
        const earliestEnd = endDates.length ? dayjs(endDates.reduce((a, b) => (a < b ? a : b))) : undefined;
        const latestStart = startDates.length ? dayjs(startDates.reduce((a, b) => (a > b ? a : b))) : undefined;
        return (
          <BulkActionDialog
            action={bulkSelection.bulkDialogAction}
            saving={bulkSelection.bulkSaving}
            onClose={() => bulkSelection.setBulkDialogAction(null)}
            onSave={handleBulkSave}
            maxDate={bulkSelection.bulkDialogAction === 'start_date' ? earliestEnd : undefined}
            minDate={bulkSelection.bulkDialogAction === 'end_date' ? latestStart : undefined}
          />
        );
      })()}

      {editingBooking && (
        <BookingModal
          machines={machines}
          sites={sites}
          subcontractors={subcontractors}
          booking={editingBooking}
          lockedMachineId={editingBooking.machine_id}
          prefilledStartDate={null}
          defaultStartTime={settings.default_start_time}
          defaultEndTime={settings.default_end_time}
          activeProjects={activeProjects}
          canCreateSite={canEditMachines}
          onClose={() => setEditingBooking(null)}
          onSave={(_, data) => {
            updateBooking(editingBooking.id, data)
              .then(() => notifySuccess(LABELS.bookingUpdateSuccess))
              .catch(() => notifyError(LABELS.mutationError));
          }}
          onAddSubcontractor={handleAddSubcontractor}
          onAddSubcontractorUser={handleAddSubcontractorUser}
          onAddSite={handleAddSite}
        />
      )}
    </div>
  );
}
